import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';


const EMPTY_DIAGNOSTICS = {
  summary: {
    physical_templates: 0,
    effective_templates: 0,
    duplicate_scopes: 0,
    location_legacy_variants: 0,
    legacy_module_templates: 0,
    hidden_templates: 0,
    contract_items: 0,
    derived_contract_items: 0,
    print_setting_warnings: 0,
  },
  duplicate_scopes: [],
  location_legacy_scopes: [],
  print_preset_warnings: [],
};


export const TemplateCockpitDiagnosticsPanel = ({
  diagnostics = EMPTY_DIAGNOSTICS,
  title = 'Diagnóstico governado',
  description = 'Observabilidade do cockpit antes de qualquer limpeza estrutural.',
  testIdPrefix = 'template-cockpit-diagnostics',
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const summary = diagnostics?.summary || EMPTY_DIAGNOSTICS.summary;
  const duplicateScopes = diagnostics?.duplicate_scopes || [];
  const legacyScopes = diagnostics?.location_legacy_scopes || [];
  const warnings = diagnostics?.print_preset_warnings || [];

  return (
    <Card data-testid={`${testIdPrefix}-panel`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm" data-testid={`${testIdPrefix}-title`}>{title}</CardTitle>
            <CardDescription data-testid={`${testIdPrefix}-description`}>{description}</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails((prev) => !prev)}
              data-testid={`${testIdPrefix}-toggle-details-button`}
            >
              {showDetails ? 'Ocultar detalhes' : 'Mostrar detalhes'}
            </Button>
            {showDetails && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLegacy((prev) => !prev)}
                data-testid={`${testIdPrefix}-toggle-legacy-button`}
              >
                {showLegacy ? 'Ocultar legado' : 'Mostrar legado'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5" data-testid={`${testIdPrefix}-summary-grid`}>
          {[
            { key: 'physical', label: 'Físicos', value: summary.physical_templates },
            { key: 'effective', label: 'Efectivos', value: summary.effective_templates },
            { key: 'duplicates', label: 'Escopos duplicados', value: summary.duplicate_scopes },
            { key: 'legacy', label: 'Legado localização', value: summary.location_legacy_variants },
            { key: 'contract', label: 'Itens contrato', value: summary.contract_items },
          ].map((item) => (
            <div key={item.key} className="rounded-xl border bg-slate-50 px-3 py-2" data-testid={`${testIdPrefix}-summary-${item.key}`}>
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</p>
              <p className="mt-1 text-lg font-bold leading-none">{item.value || 0}</p>
            </div>
          ))}
        </div>

        {showDetails && <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-xl border p-3" data-testid={`${testIdPrefix}-duplicates-card`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">Escopos duplicados</p>
                <p className="text-xs text-muted-foreground">Listados sem fundir nem apagar nesta fase.</p>
              </div>
              <Badge variant="outline" data-testid={`${testIdPrefix}-duplicates-count-badge`}>{duplicateScopes.length}</Badge>
            </div>
            <div className="mt-3 space-y-2">
              {duplicateScopes.length > 0 ? duplicateScopes.slice(0, 6).map((scope) => (
                <div key={scope.scope_key} className="rounded-lg border bg-muted/30 px-3 py-2" data-testid={`${testIdPrefix}-duplicate-scope-${scope.scope_key.replace(/[^a-zA-Z0-9]+/g, '-')}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">{scope.document_family}</p>
                    <Badge variant="secondary">{scope.origin_context}</Badge>
                    <Badge variant="outline">{scope.location_scope_label}</Badge>
                    <Badge className="bg-amber-100 text-amber-900">{scope.count} físico(s)</Badge>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">{(scope.names || []).join(' · ')}</p>
                </div>
              )) : (
                <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-duplicates-empty`}>
                  Sem escopos duplicados detectados.
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border p-3" data-testid={`${testIdPrefix}-warnings-card`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold">Warnings de presets</p>
                <Badge variant="outline" data-testid={`${testIdPrefix}-warnings-count-badge`}>{warnings.length}</Badge>
              </div>
              <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                {warnings.length > 0 ? warnings.slice(0, 4).map((warning, index) => (
                  <div key={`${warning.code}-${index}`} className="rounded-lg border bg-amber-50 px-3 py-2" data-testid={`${testIdPrefix}-warning-${index}`}>
                    <p className="font-medium text-amber-900">{warning.title}</p>
                    <p className="mt-1">{warning.description}</p>
                  </div>
                )) : (
                  <p data-testid={`${testIdPrefix}-warnings-empty`}>Sem anomalias sinalizadas nos presets.</p>
                )}
              </div>
            </div>

            {showLegacy && (
              <div className="rounded-xl border p-3" data-testid={`${testIdPrefix}-legacy-card`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">Legado por localização</p>
                  <Badge variant="outline" data-testid={`${testIdPrefix}-legacy-count-badge`}>{legacyScopes.length}</Badge>
                </div>
                <div className="mt-3 space-y-2">
                  {legacyScopes.length > 0 ? legacyScopes.slice(0, 6).map((scope, index) => (
                    <div key={`${scope.location_scope_label}-${index}`} className="rounded-lg border bg-muted/30 px-3 py-2 text-xs" data-testid={`${testIdPrefix}-legacy-scope-${index}`}>
                      <p className="font-medium">{scope.document_family} · {scope.location_scope_label}</p>
                      <p className="mt-1 text-muted-foreground">{(scope.names || []).join(' · ')}</p>
                    </div>
                  )) : (
                    <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-legacy-empty`}>
                      Sem variantes locais classificadas como legado.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>}
      </CardContent>
    </Card>
  );
};